import { useNotification } from "@context/NotificationContext";
import { LANG_BASE } from "@features/vessel-management/components/RegisteredVessels/RegisteredVessels";
import { getVesselRegisterFormSchema } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import type { Vessel, VesselRequestPayload } from "@services/vessel/types";
import {
  discardDraftVessel,
  patchDraftVessel,
  postDraftVessel,
  postVessel,
  putVessel,
} from "@services/vesselsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import mapVesselRegistrationFormToPayload from "@utils/mappers/vesselRegistrationMapper";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

interface UseVesselRegistrationProps {
  formValues?: z.infer<ReturnType<typeof getVesselRegisterFormSchema>>;
  resetForm?: () => void;
  vesselId?: string;
  onSubmitError?: (error: unknown, variables: SubmitVesselMutationVariables) => void;
  onSaveError?: (error: unknown, variables: SaveDraftMutationVariables) => void;
}

interface UseVesselRegistrationResult {
  onSubmit: (options?: { isMarineDepartment?: boolean }) => Promise<void>;
  onSave: () => Promise<void>;
  onDiscard: () => Promise<void>;
}

type SubmitVesselMutationVariables = {
  payload: VesselRequestPayload;
  id?: string;
  isMarineDepartment?: boolean;
};

type SaveDraftMutationVariables = {
  payload: VesselRequestPayload;
  id?: string;
};

export const useVesselRegistration = ({
  formValues,
  resetForm,
  vesselId,
  onSubmitError,
  onSaveError,
}: UseVesselRegistrationProps): UseVesselRegistrationResult => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { t } = useTranslation();

  const getVesselDetails = () =>
    (
      (state as {
        vesselDetails?: Vessel;
      } | null) ?? { vesselDetails: undefined }
    ).vesselDetails;

  const registerVessel = useMutation<unknown, unknown, SubmitVesselMutationVariables>({
    mutationFn: ({ payload, id }) => {
      return runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselRegistration",
          action: id ? "update_vessel_registration" : "submit_vessel_registration",
          entityType: "vessel",
        },
        (transactionId) =>
          id
            ? putVessel({ id, vesselPayload: payload, ...(transactionId ? { transactionId } : {}) })
            : postVessel({ vesselPayload: payload, ...(transactionId ? { transactionId } : {}) }),
      );
    },
    onSuccess: (_, { isMarineDepartment }) => {
      notify({
        title: isMarineDepartment
          ? t(`${LANG_BASE}.notifications.vessel-submitted-success-title-md`)
          : t(`${LANG_BASE}.notifications.vessel-submitted-success-title`),
        description: isMarineDepartment
          ? undefined
          : t(`${LANG_BASE}.notifications.vessel-submitted-success-description`),
        type: "success",
      });

      resetForm && resetForm();
      qc.invalidateQueries({ queryKey: ["registeredVessels"] });
      navigate("/vessel-management");
    },
    onError: (error: any, variables) => {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-submitted-error-title`),
        description: error?.response?.data?.detail ?? error.message,
        type: "error",
      });
      onSubmitError?.(error, variables);
    },
  });

  const saveDraft = useMutation<unknown, unknown, SaveDraftMutationVariables>({
    mutationFn: ({ payload, id }) => {
      return runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselRegistration",
          action: "save_vessel_registration_draft",
          entityType: "vessel",
        },
        (transactionId) =>
          id
            ? patchDraftVessel({
                vesselPayload: payload,
                id,
                ...(transactionId ? { transactionId } : {}),
              })
            : postDraftVessel({
                vesselPayload: payload,
                ...(transactionId ? { transactionId } : {}),
              }),
      );
    },
    onSuccess: () => {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-draft-saved-success-title`),
      });
      resetForm && resetForm();
      qc.invalidateQueries({ queryKey: ["registeredVessels"] });
      navigate("/vessel-management");
    },
    onError: (error: any, variables) => {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-draft-saved-error-title`),
        description: error?.response?.data?.detail ?? error.message,
        type: "error",
      });
      onSaveError?.(error, variables);
    },
  });

  const discardDraft = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselRegistration",
          action: "discard_vessel_registration_draft",
          entityType: "vessel",
        },
        (transactionId) => discardDraftVessel({ id, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["registeredVessels"] });
      navigate("/vessel-management");
    },
    onError: (error: any) => {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-draft-discard-error-title`),
        description: error?.response?.data?.detail ?? error.message ?? t("common.try-again-later"),
        type: "error",
      });
    },
  });

  const onSubmit = async (options?: { isMarineDepartment?: boolean }): Promise<void> => {
    if (!formValues) return;

    const vesselDetails = getVesselDetails();
    const isMarineDepartment = options?.isMarineDepartment;
    const shouldUpdateExistingVessel = !!vesselDetails?.id && !vesselDetails.markedAsDraft;

    const registeredVessel: VesselRequestPayload = mapVesselRegistrationFormToPayload(
      formValues,
      vesselDetails,
    );

    if (shouldUpdateExistingVessel) {
      registerVessel.mutate({
        payload: registeredVessel,
        id: vesselDetails.id,
        isMarineDepartment,
      });
    } else {
      registerVessel.mutate({ payload: registeredVessel, isMarineDepartment });
    }
  };

  const onSave = async (): Promise<void> => {
    if (!formValues) return;

    const vesselDetails = getVesselDetails();

    const registeredVessel: VesselRequestPayload = mapVesselRegistrationFormToPayload(
      formValues,
      vesselDetails,
      true,
    );

    if (vesselDetails?.id) {
      saveDraft.mutate({
        payload: registeredVessel,
        id: vesselDetails.id,
      });
    } else {
      saveDraft.mutate({ payload: registeredVessel });
    }
  };

  const onDiscard = async (): Promise<void> => {
    const vesselDetails = getVesselDetails();
    const id = vesselId ?? vesselDetails?.id;

    await discardDraft.mutateAsync({ id: id! });
  };

  return {
    onSubmit,
    onSave,
    onDiscard,
  };
};
